/* eslint-disable @typescript-eslint/no-explicit-any */
// src/components/sale-page/SalePageRenderer.tsx
// Server component that renders a published sale page
'use client'

import Image from 'next/image'
import { useState, useEffect, useCallback, useRef } from 'react'
import type { PageHeader, PageFooter, PageTheme, PageTracking, SalePageSection, CartItem, CookieConsentConfig, ContainerSize } from '@/../types/sale-page'
import { DEFAULT_THEME, DEFAULT_COOKIE_CONSENT, CONTAINER_SIZES } from '@/../types/sale-page'
import TrackingScripts, { trackAddToCart, trackInitiateCheckout } from './TrackingScripts'
import CookieConsent, { getCookieConsent } from './CookieConsent'
import InlineCheckout from './InlineCheckout'
import FloatingSocials from './FloatingSocials'
import CopyCartButton from './CopyCartButton'
import InlineThankYou from './InlineThankYou'
import { DEFAULT_THANKYOU } from '@/../types/sale-page'
import type { ThankYouPageConfig } from '@/../types/sale-page'
import { availablePieces, piecesPerUnit } from '@/lib/stockMode'

interface ProductVariant {
  id: string
  name: string
  price: number
  stock: number
  image: string | null
  options: Record<string, string>
  unitCount?: number | null
}

interface Product {
  id: string
  name: string
  price: number
  image: string | null
  stock: number
  description?: string | null
  options?: Array<{ name: string; values: string[] }> | null
  variants?: ProductVariant[]
  // โหมดสต๊อก — ต้องมีครบ ไม่งั้นตัดสิน "ของหมด" ผิด (ดู stockOf ด้านล่าง)
  checkStock?: boolean | null
  variantStockShared?: boolean | null
  unitCount?: number | null
}

// ── ของหมดหรือยัง ────────────────────────────────────────────────────
// ⚠️ เคยเขียนเป็น `variant.stock <= 0` ตรง ๆ ซึ่งผิด 2 ทาง และทั้งคู่ให้
// อาการเดียวกันคือ "ของมีอยู่แต่หน้าเว็บบอกหมด" (ลูกค้าสั่งไม่ได้เลย):
//
//   1. ร้านที่ไม่ได้นับสต๊อก (checkStock = false ซึ่งเป็นค่าเริ่มต้น) ปล่อยช่อง
//      สต๊อกเป็น 0 ไว้ — ของมีเต็มร้านแต่หน้าเว็บขึ้นหมดทุกตัว
//   2. โหมดสต๊อก "รวม" (variantStockShared) — ทุกตัวเลือกใช้คลังของสินค้า
//      ส่วน ProductVariant.stock **ไม่ถูกใช้เลย** (ฟอร์มซ่อนช่องนั้นด้วยซ้ำ)
//      จึงเป็น 0 ตลอด → ทุกตัวเลือกขึ้น "หมด"
//
// ฝั่งเซิร์ฟเวอร์ (/api/public/checkout) ตัดสินด้วย lib/stockMode.ts ถูกอยู่แล้ว
// หน้าเว็บจึงบล็อกลูกค้าทั้งที่สั่งได้จริง — ใช้ helper ตัวเดียวกันเพื่อไม่ให้
// ตรรกะ 2 ฝั่งแยกกันอีก
function stockOf(product: Product, variant?: ProductVariant | null): number {
  // ร้านไม่นับสต๊อก = ไม่มีวันหมด (Infinity เพื่อให้เทียบ > 0 ผ่านเสมอ)
  if (product.checkStock !== true) return Number.POSITIVE_INFINITY
  return availablePieces(product, variant ?? undefined)
}

/** ชิ้นที่ต้องใช้ต่อ 1 หน่วยขาย — แพ็ค 3 กระปุกในโหมดรวมกินคลัง 3 ชิ้น */
function piecesNeeded(product: Product, variant?: ProductVariant | null): number {
  if (product.checkStock !== true) return 0
  return piecesPerUnit(product, variant ?? undefined)
}

/** ตัวเลือกนี้ยังสั่งได้ไหม (มีของพอสำหรับอย่างน้อย 1 หน่วย) */
function variantInStock(product: Product, variant: ProductVariant): boolean {
  return stockOf(product, variant) >= Math.max(1, piecesNeeded(product, variant))
}

interface Shop {
  id: string
  name: string
  logo: string | null
  phone: string | null
  facebookUrl: string | null
  instagramUrl?: string | null
  tiktokUrl?: string | null
  lineId: string | null
  bankCode?: string | null
  bankName?: string | null
  bankAccount?: string | null
  bankAccountName?: string | null
  promptPayId?: string | null
  qrCodeImage?: string | null
}

interface Props {
  page: {
    id?: string
    title: string
    slug?: string
    sections: unknown[]
    header: Record<string, unknown> | null
    footer: Record<string, unknown> | null
    theme: Record<string, unknown> | null
    tracking?: Record<string, unknown> | null
    cookieConsent?: Record<string, unknown> | null
    cartEnabled: boolean
    checkoutMode: string
    checkoutLayout?: string
    floatingSocialPosition?: string
    floatingSocialIcons?: { line?: boolean; facebook?: boolean; instagram?: boolean; tiktok?: boolean; phone?: boolean } | null
    /** สีพื้นหลังปุ่มเมนูติดต่อลอย — null = ใช้สี default ของคอมโพเนนต์ */
    floatingSocialButtonColor?: string | null
    allowCOD?: boolean
    allowTransfer?: boolean
    transferShowAccount?: boolean
    transferShowQr?: boolean
    couponEnabled?: boolean
    /** Max total pieces per order. null/0 = unlimited. = 1 → "ซื้อสินค้า"
     *  buy-now flow (replace cart + go straight to checkout). */
    maxItemsPerOrder?: number | null
    /** Hex colour for the "เพิ่มลงตะกร้า" + "ยืนยันสั่งซื้อ" buttons.
     *  Optional — falls back to theme.primaryColor when unset.
     *  null = DB row with the colour cleared (ใช้สีตาม theme). */
    orderButtonColor?: string | null
    thankYouPage?: Record<string, unknown> | null
  }
  shop: Shop
  products: Product[]
}

export default function SalePageRenderer({ page, shop, products }: Props) {
  const theme = { ...DEFAULT_THEME, ...(page.theme || {}) } as PageTheme
  const header = page.header as unknown as PageHeader | null
  const footer = page.footer as unknown as PageFooter | null
  const sections = (page.sections || []) as SalePageSection[]
  const tracking = (page.tracking || null) as PageTracking | null
  const cookieConfig = { ...DEFAULT_COOKIE_CONSENT, ...(page.cookieConsent || {}) } as CookieConsentConfig

  // Cookie consent state: if enabled, block tracking until accepted
  const [cookieAccepted, setCookieAccepted] = useState(() => {
    if (!cookieConfig.enabled) return true // No consent required
    return getCookieConsent() === 'accepted'
  })

  // Google Fonts URL
  const fontUrl = theme.fontFamily
    ? `https://fonts.googleapis.com/css2?family=${encodeURIComponent(theme.fontFamily)}:wght@300;400;500;600;700&display=swap`
    : null

  // Cart with localStorage persistence (24h expiry)
  const cartKey = `fc-cart-${shop.id}`
  const [cart, setCart] = useState<CartItem[]>(() => {
    if (typeof window === 'undefined') return []
    try {
      const stored = localStorage.getItem(cartKey)
      if (!stored) return []
      const { items, expiry } = JSON.parse(stored)
      if (Date.now() > expiry) { localStorage.removeItem(cartKey); return [] }
      return items || []
    } catch { return [] }
  })
  const [cartOpen, setCartOpen] = useState(false)

  // Save cart to localStorage on change
  useEffect(() => {
    if (cart.length > 0) {
      localStorage.setItem(cartKey, JSON.stringify({ items: cart, expiry: Date.now() + 24 * 60 * 60 * 1000 }))
    } else {
      localStorage.removeItem(cartKey)
    }
  }, [cart, cartKey])
  const [variantPickerProduct, setVariantPickerProduct] = useState<Product | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [checkoutSuccess, setCheckoutSuccess] = useState<any>(null)
  const checkoutRef = useRef<HTMLDivElement>(null)

  const thankYouConfig = { ...DEFAULT_THANKYOU, ...(page.thankYouPage || {}) } as ThankYouPageConfig
  const isInlineCheckout = page.checkoutLayout === 'inline'

  const productMap = new Map(products.map(p => [p.id, p]))

  // Max pieces per order. null/0 = unlimited. = 1 → buy-now: the card button
  // reads "ซื้อสินค้า", and a click replaces the cart with that one item and
  // goes straight to checkout (scroll inline / redirect separate-page). The
  // customer can navigate back to swap the product.
  const maxItems = page.maxItemsPerOrder && page.maxItemsPerOrder >= 1 ? page.maxItemsPerOrder : null
  const singleItemMode = maxItems === 1

  const addToCart = (product: Product) => {
    // If product has variants → show picker popup instead of adding directly
    if (product.variants && product.variants.length > 0) {
      setVariantPickerProduct(product)
      return
    }

    addToCartDirect(product.id, product.name, product.price, product.image || undefined)
  }

  const addToCartWithVariant = (product: Product, variant: ProductVariant) => {
    const cartKey = `${product.id}__${variant.id}`
    const displayName = `${product.name} (${variant.name})`
    const item: CartItem = {
      productId: cartKey,
      name: displayName,
      price: variant.price,
      image: variant.image || product.image || undefined,
      quantity: 1,
    }
    setVariantPickerProduct(null)
    trackAddToCart({ id: cartKey, name: displayName, price: variant.price, variant: variant.name })
    if (singleItemMode) { buyNow(item); return }
    setCart(prev => {
      if (maxItems && prev.reduce((s, i) => s + i.quantity, 0) >= maxItems) return prev
      const existing = prev.find(i => i.productId === cartKey)
      if (existing) return prev.map(i => i.productId === cartKey ? { ...i, quantity: i.quantity + 1 } : i)
      return [...prev, item]
    })
  }

  const addToCartDirect = (id: string, name: string, price: number, image?: string) => {
    const item: CartItem = { productId: id, name, price, image, quantity: 1 }
    trackAddToCart({ id, name, price })
    if (singleItemMode) { buyNow(item); return }
    setCart(prev => {
      if (maxItems && prev.reduce((s, i) => s + i.quantity, 0) >= maxItems) return prev
      const existing = prev.find(i => i.productId === id)
      if (existing) return prev.map(i => i.productId === id ? { ...i, quantity: i.quantity + 1 } : i)
      return [...prev, item]
    })
  }

  // Buy-now (max 1/order): the single item IS the whole order; replace the
  // cart and jump to checkout.
  const buyNow = (item: CartItem) => {
    setCart([item])
    if (isInlineCheckout) {
      setCartOpen(false)
      setTimeout(() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60)
    } else if (page.slug && shop.id) {
      sessionStorage.setItem(`cart-${shop.id}-${page.slug}`, JSON.stringify([item]))
      window.location.href = `/s/${shop.id}/${page.slug}/checkout?salePageId=${page.id || ''}`
    }
  }

  const updateQuantity = (productId: string, delta: number) => {
    setCart(prev => prev
      .map(i => i.productId === productId ? { ...i, quantity: Math.max(0, i.quantity + delta) } : i)
      .filter(i => i.quantity > 0)
    )
  }

  const cartTotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0)
  const cartCount = cart.reduce((sum, i) => sum + i.quantity, 0)

  // ตัวเลขบนตะกร้าเด้งทุกครั้งที่จำนวนเพิ่ม — ให้ลูกค้าเห็นว่า "เข้าตะกร้าแล้ว"
  // แม้ตะกร้าจะอยู่มุมบนไกลจากปุ่มที่เพิ่งกด
  const [cartBump, setCartBump] = useState(false)
  const prevCartCount = useRef(cartCount)
  useEffect(() => {
    if (cartCount > prevCartCount.current) {
      setCartBump(true)
      const t = setTimeout(() => setCartBump(false), 420)
      prevCartCount.current = cartCount
      return () => clearTimeout(t)
    }
    prevCartCount.current = cartCount
  }, [cartCount])

  // Navbar ตอนเลื่อนลง: จางเฉพาะ "พื้นหลัง + โลโก้ + ชื่อร้าน + เมนู" เหลือแค่
  // ตะกร้าลอยอยู่ พอเลื่อนกลับขึ้นบนสุดค่อยคืนสภาพเดิม (ทำเฉพาะ header แบบ
  // sticky — แบบไม่ sticky มันเลื่อนหายไปกับหน้าอยู่แล้ว)
  const [navFaded, setNavFaded] = useState(false)
  useEffect(() => {
    if (!header?.enabled || !header?.sticky) return
    const onScroll = () => setNavFaded(window.scrollY > 24)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [header?.enabled, header?.sticky])

  const handleCheckoutChat = () => {
    // Track checkout event
    trackInitiateCheckout(
      cart.map(i => ({ id: i.productId, name: i.name, price: i.price, quantity: i.quantity })),
      cartTotal
    )

    const items = cart.map(i => `${i.name} x${i.quantity}`).join(', ')
    const message = encodeURIComponent(`สนใจสั่ง: ${items} รวม ${cartTotal.toLocaleString()}฿`)

    if (shop.facebookUrl) {
      window.open(`https://m.me/${shop.facebookUrl.replace(/.*facebook.com\//, '')}?ref=${message}`, '_blank')
    } else if (shop.lineId) {
      window.open(`https://line.me/R/ti/p/${shop.lineId}`, '_blank')
    } else if (shop.phone) {
      window.open(`tel:${shop.phone}`)
    }
  }

  const handleCheckoutDirect = () => {
    if (isInlineCheckout) {
      setCartOpen(false)
      setTimeout(() => {
        checkoutRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 50)
    } else {
      if (page.slug && shop.id) {
        sessionStorage.setItem(`cart-${shop.id}-${page.slug}`, JSON.stringify(cart))
        window.location.href = `/s/${shop.id}/${page.slug}/checkout?salePageId=${page.id || ''}`
      }
    }
  }

  const s3Domain = process.env.NEXT_PUBLIC_S3_DOMAIN || ''

  const getImageUrl = (url: string | null | undefined) => {
    if (!url) return ''
    if (url.startsWith('http://') || url.startsWith('https://')) return url
    return s3Domain ? `${s3Domain.replace(/\/$/, '')}/${url.replace(/^\//, '')}` : url
  }

  const borderRadiusClass = {
    none: 'rounded-none', sm: 'rounded-sm', md: 'rounded-lg', lg: 'rounded-2xl', full: 'rounded-full',
  }[theme.borderRadius] || 'rounded-lg'

  // ระยะห่างซ้าย-ขวาของ container ทั้งเพจ — เดิม header/checkout hardcode px-4
  // ส่วน section ไม่มีเลย (เนื้อหาชนขอบจอบนมือถือ) ตอนนี้ตั้งได้จากแท็บ Theme
  const containerPadX = theme.containerPaddingX ?? 16

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: theme.backgroundColor, fontFamily: `'${theme.fontFamily}', sans-serif` }}>
      {/* Google Font */}
      {fontUrl && (
        // eslint-disable-next-line @next/next/no-page-custom-font
        <link rel="stylesheet" href={fontUrl} />
      )}

      {/* Tracking Scripts (blocked until cookie consent) */}
      <TrackingScripts tracking={tracking} consentGiven={cookieAccepted} />

      {/* Header — background + border live on the <header> element
          (not on the inner max-width container) so when sticky kicks
          in on wide screens the bar fills the viewport horizontally
          instead of stopping at the container's 1140px and leaving
          page content visible peeking through on the sides. */}
      {header?.enabled && (
        <header
          className={`${header.sticky ? 'sticky top-0 z-40' : ''} transition-[background-color,box-shadow,border-color] duration-300`}
          style={{
            // จางลง = พื้นหลัง/เงา/เส้นคั่นหายไป เหลือแต่ตะกร้าลอยเหนือเนื้อหา
            backgroundColor: navFaded ? 'transparent' : (header.backgroundColor || '#fff'),
            color: header.textColor || '#1f2937',
            boxShadow: navFaded ? 'none' : '0 1px 2px rgba(0,0,0,0.05)',
            borderBottom: `1px solid ${navFaded ? 'transparent' : '#f3f4f6'}`,
          }}
        >
          <div
            className="mx-auto flex items-center justify-between py-3"
            style={{
              maxWidth: CONTAINER_SIZES[theme.defaultContainer || 'xl']?.maxWidth || '1140px',
              paddingInline: containerPadX,
            }}
          >
            <div
              className="flex items-center gap-3 transition-opacity duration-300"
              style={{ opacity: navFaded ? 0 : 1, pointerEvents: navFaded ? 'none' : undefined }}
            >
              {header.showLogo !== false && (header.logo || shop.logo) && (
                <Image src={getImageUrl(header.logo as string || shop.logo)} alt={shop.name} width={40} height={40} className="object-contain" />
              )}
              {header.showShopName !== false && <span className="font-bold text-lg">{header.shopName || shop.name}</span>}
            </div>
            <div className="flex items-center gap-4">
              {header.navLinks && (header.navLinks as any[]).map((link: any, i: number) => (
                <a
                  key={i}
                  href={link.href}
                  className="text-sm hover:opacity-70 hidden sm:block transition-opacity duration-300"
                  style={{ opacity: navFaded ? 0 : 1, pointerEvents: navFaded ? 'none' : undefined }}
                >
                  {link.label}
                </a>
              ))}
              {header.showCart && page.cartEnabled && (
                <div className="relative">
                  <button
                    onClick={() => setCartOpen(!cartOpen)}
                    className="relative w-10 h-10 rounded-full flex items-center justify-center shadow-md transition-opacity hover:opacity-90 active:scale-95"
                    style={{ backgroundColor: header.cartButtonColor || '#2563EB' }}
                  >
                    {/* CSS-masked SVG so the white-on-button colour
                        works regardless of the file's stroke colour —
                        the SVG acts as a mask cutout and the div's
                        backgroundColor fills it. Defaults to cart-01
                        when admin hasn't picked a variant. */}
                    <span
                      className="w-5 h-5 inline-block"
                      style={{
                        backgroundColor: 'white',
                        WebkitMaskImage: `url(/icons/carts/${header.cartIcon || 'cart-01'}.svg)`,
                        maskImage: `url(/icons/carts/${header.cartIcon || 'cart-01'}.svg)`,
                        WebkitMaskRepeat: 'no-repeat',
                        maskRepeat: 'no-repeat',
                        WebkitMaskPosition: 'center',
                        maskPosition: 'center',
                        WebkitMaskSize: 'contain',
                        maskSize: 'contain',
                      }}
                      aria-hidden="true"
                    />
                    {cartCount > 0 && (
                      <span className={`absolute -top-1 -right-1 w-5 h-5 text-[10px] font-bold text-white rounded-full flex items-center justify-center bg-red-500 shadow-sm ${cartBump ? 'animate-cart-bump' : ''}`}>
                        {cartCount > 99 ? '99+' : cartCount}
                      </span>
                    )}
                  </button>

                  {/* Cart Dropdown - anchored to this icon */}
                  {cartOpen && (
                    <>
                      <div className="fixed inset-0 z-40" onClick={() => setCartOpen(false)} />
                      <div className="absolute right-0 top-full mt-2 z-50 w-[90vw] sm:w-96 animate-cart-in">
                        <CartDropdownContent
                          cart={cart}
                          cartCount={cartCount}
                          cartTotal={cartTotal}
                          theme={theme}
                          shopId={shop.id}
                          salePageId={page.id}
                          getImageUrl={getImageUrl}
                          updateQuantity={updateQuantity}
                          onCheckout={() => { setCartOpen(false); handleCheckoutDirect() }}
                          onClose={() => setCartOpen(false)}
                        />
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </header>
      )}

      {/* Main Content */}
      <main className="flex-1">
        {checkoutSuccess ? (
          // Inline Thank You
          <InlineThankYou
            order={checkoutSuccess.order}
            bankInfo={checkoutSuccess.bankInfo}
            config={thankYouConfig}
            theme={theme}
            getImageUrl={getImageUrl}
            onBack={() => {
              setCheckoutSuccess(null)
              setCart([])
            }}
          />
        ) : (
          <>
            {/* Normal sections */}
            {sections.filter(s => s.visible !== false).map((section) => {
              const containerSize = section.container || theme.defaultContainer || 'xl'
              const isFullWidth = containerSize === 'full' || section.type === 'hero' || section.type === 'divider'
              return (
                <div key={section.id} style={{ paddingTop: section.spacing?.top || 0, paddingBottom: section.spacing?.bottom || 0 }}>
                  {isFullWidth ? (
                    renderSection(section, { productMap, addToCart, theme, borderRadiusClass, orderButtonColor: page.orderButtonColor, getImageUrl, singleItemMode })
                  ) : (
                    <div style={{ maxWidth: CONTAINER_SIZES[containerSize]?.maxWidth || '1140px', marginInline: 'auto', paddingInline: section.paddingX ?? containerPadX }}>
                      {renderSection(section, { productMap, addToCart, theme, borderRadiusClass, orderButtonColor: page.orderButtonColor, getImageUrl, singleItemMode })}
                    </div>
                )}
              </div>
              )
            })}

            {/* Inline Checkout — shown at bottom when checkoutLayout === 'inline' */}
            {isInlineCheckout && (
              <div ref={checkoutRef} style={{ maxWidth: CONTAINER_SIZES['xl']?.maxWidth || '1140px', marginInline: 'auto', paddingInline: containerPadX }} className="py-8">
                <InlineCheckout
                  cart={cart}
                  cartTotal={cartTotal}
                  shopId={shop.id}
                  salePageId={page.id || ''}
                  pageSlug={page.slug || ''}
                  allowCOD={page.allowCOD !== false}
                  allowTransfer={page.allowTransfer !== false}
                  transferShowAccount={page.transferShowAccount !== false}
                  transferShowQr={page.transferShowQr === true}
                  couponEnabled={page.couponEnabled || false}
                  bankInfo={{
                    bankCode: shop.bankCode,
                    bankName: shop.bankName,
                    bankAccount: shop.bankAccount,
                    bankAccountName: shop.bankAccountName,
                    promptPayId: shop.promptPayId,
                    qrCodeImage: shop.qrCodeImage ? getImageUrl(shop.qrCodeImage) : null,
                  }}
                  theme={theme}
                  orderButtonColor={page.orderButtonColor}
                  getImageUrl={getImageUrl}
                  onUpdateQty={updateQuantity}
                  maxItems={maxItems}
                  onSuccess={(data) => {
                    // Clear the cart on order success (not only on the
                    // InlineThankYou "back" tap) — otherwise closing the tab
                    // mid-thank-you left the items in localStorage and the cart
                    // reappeared on the next visit.
                    setCart([])
                    setCheckoutSuccess(data)
                  }}
                  onBack={() => {}}
                />
              </div>
            )}
          </>
        )}
      </main>

      {/* Footer — สี/พื้นหลังอยู่บน <footer> ไม่ใช่กล่อง max-width ด้านใน ไม่งั้น
          บนจอกว้างแถบสีจะหยุดที่ container แล้วเห็นพื้นเพจโผล่ข้างๆ (ปัญหาเดียว
          กับที่แก้ไปแล้วฝั่ง header) */}
      {footer?.enabled && (
        <footer style={{ backgroundColor: footer.backgroundColor || '#1f2937', color: footer.textColor || '#f3f4f6' }}>
          <div
            className="mx-auto py-8 text-center"
            style={{
              maxWidth: CONTAINER_SIZES[theme.defaultContainer || 'xl']?.maxWidth || '1140px',
              paddingInline: containerPadX,
            }}
          >
            {footer.text && <p className="text-sm">{footer.text}</p>}
            {footer.socialLinks && (
              <div className="flex justify-center gap-4 mt-4">
                {footer.socialLinks.facebook && <a href={footer.socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="hover:opacity-70">Facebook</a>}
                {footer.socialLinks.line && <a href={`https://line.me/R/ti/p/${footer.socialLinks.line}`} target="_blank" rel="noopener noreferrer" className="hover:opacity-70">LINE</a>}
                {footer.socialLinks.phone && <a href={`tel:${footer.socialLinks.phone}`} className="hover:opacity-70">{footer.socialLinks.phone}</a>}
              </div>
            )}
          </div>
        </footer>
      )}

      {/* เครดิตระบบ — อยู่ใต้ footer ของร้าน (โชว์เสมอ แม้ร้านปิด footer) ใช้สี
          จางๆ บนพื้นเพจ เพื่อให้อ่านออกว่าเป็นของแพลตฟอร์ม ไม่ใช่ข้อความของร้าน */}
      <div className="py-3 text-center text-xs" style={{ color: '#9ca3af' }}>
        Powered by{' '}
        <a
          href="https://seaaii.com"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold hover:underline"
          style={{ color: theme.primaryColor }}
        >
          Seaaii
        </a>
      </div>

      {/* Variant Picker Popup */}
      {variantPickerProduct && (
        <VariantPickerPopup
          product={variantPickerProduct}
          theme={theme}
          getImageUrl={getImageUrl}
          singleItemMode={singleItemMode}
          onSelect={(variant) => addToCartWithVariant(variantPickerProduct, variant)}
          onClose={() => setVariantPickerProduct(null)}
        />
      )}

      {/* Popups */}
      {sections
        .filter(s => s.type === 'popup' && s.visible !== false)
        .map(s => (
          <PopupBlock key={s.id} section={s} theme={theme} getImageUrl={getImageUrl} />
        ))
      }

      {/* เมนูติดต่อลอย (LINE / FB / IG / TikTok / โทร) — กดปุ่มเดียวแล้วกางขึ้น
          ปุ่มตะกร้าลอยของเดิมถูกถอดออกแล้ว เพราะซ้ำกับตะกร้าบน navbar
          (navbar sticky อยู่ตลอด ตะกร้าจึงกดได้ทุกจุดที่เลื่อนอยู่แล้ว) */}
      <FloatingSocials
        shop={shop}
        position={page.floatingSocialPosition === 'right' ? 'right' : 'left'}
        visible={page.floatingSocialIcons}
        buttonColor={page.floatingSocialButtonColor}
      />

      {/* กันเหนียว: เพจที่ปิด header หรือปิดตะกร้าบน navbar จะไม่เหลือทางเข้าตะกร้า
          เลยหลังถอดปุ่มลอยออก — เฉพาะกรณีนั้นถึงโชว์ปุ่มตะกร้าลอย + แผงตะกร้า
          (แผงบน navbar ผูกกับปุ่มใน header ซึ่งไม่ถูก render ในเคสนี้) */}
      {page.cartEnabled && cartOpen && !(header?.enabled && header?.showCart) && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setCartOpen(false)} />
          <div className="fixed bottom-24 right-4 z-50 w-[90vw] sm:w-96 animate-cart-in">
            <CartDropdownContent
              cart={cart}
              cartCount={cartCount}
              cartTotal={cartTotal}
              theme={theme}
              shopId={shop.id}
              salePageId={page.id}
              getImageUrl={getImageUrl}
              updateQuantity={updateQuantity}
              onCheckout={() => { setCartOpen(false); handleCheckoutDirect() }}
              onClose={() => setCartOpen(false)}
            />
          </div>
        </>
      )}
      {page.cartEnabled && cartCount > 0 && !cartOpen && !(header?.enabled && header?.showCart) && (
        <button
          onClick={() => setCartOpen(true)}
          aria-label="ตะกร้าสินค้า"
          className="fixed bottom-6 right-6 w-14 h-14 rounded-full shadow-lg text-white flex items-center justify-center z-30"
          style={{ backgroundColor: theme.primaryColor }}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
          </svg>
          <span className={`absolute -top-1 -right-1 w-5 h-5 text-xs font-bold bg-red-500 rounded-full flex items-center justify-center ${cartBump ? 'animate-cart-bump' : ''}`}>{cartCount}</span>
        </button>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent
        config={cookieConfig}
        onAccept={() => setCookieAccepted(true)}
        onDecline={() => setCookieAccepted(false)}
      />
    </div>
  )
}

// --- Section Renderers ---

export function renderSection(
  section: SalePageSection,
  ctx: {
    productMap: Map<string, any>
    addToCart: (product: any) => void
    theme: PageTheme
    borderRadiusClass: string
    /** Optional override for the "เพิ่มลงตะกร้า" + "ยืนยันสั่งซื้อ"
     *  buttons. Falls back to ctx.theme.primaryColor when unset. */
    orderButtonColor?: string | null
    getImageUrl: (url: string | null | undefined) => string
    /** Max 1/order buy-now mode → card button reads "ซื้อสินค้า". */
    singleItemMode?: boolean
  }
) {
  switch (section.type) {
    case 'hero': return <HeroBlock section={section} theme={ctx.theme} getImageUrl={ctx.getImageUrl} />
    case 'product_grid': return <ProductGridBlock section={section} ctx={ctx} />
    case 'text': return <TextBlock section={section} />
    case 'image': return <ImageBlock section={section} getImageUrl={ctx.getImageUrl} borderRadiusClass={ctx.borderRadiusClass} />
    case 'cta': return <CTABlock section={section} theme={ctx.theme} />
    case 'countdown': return <CountdownBlock section={section} theme={ctx.theme} />
    case 'testimonial': return <TestimonialBlock section={section} borderRadiusClass={ctx.borderRadiusClass} />
    case 'faq': return <FAQBlock section={section} />
    case 'divider': return <DividerBlock section={section} />
    case 'image_slider': return <ImageSliderBlock section={section} getImageUrl={ctx.getImageUrl} />
    case 'grid': return <GridBlock section={section} theme={ctx.theme} getImageUrl={ctx.getImageUrl} />
    case 'video': return <VideoBlock section={section} />
    case 'popup': return (
      <div className="border-2 border-dashed border-purple-300 rounded-xl p-5 text-center bg-purple-50 select-none">
        <p className="text-base mb-0.5">🪟</p>
        <p className="text-sm font-medium text-purple-700">{(section as any).title || 'Popup'}</p>
        <p className="text-xs text-purple-400 mt-1">แสดงตาม trigger ที่ตั้งไว้ · ไม่แสดงใน editor</p>
      </div>
    )
    default: return null
  }
}

function HeroBlock({ section, theme, getImageUrl }: { section: any; theme: PageTheme; getImageUrl: (url: string | null) => string }) {
  return (
    <div
      className="relative py-20"
      style={{
        backgroundColor: section.backgroundColor || theme.primaryColor,
        backgroundImage: section.backgroundImage ? `url(${getImageUrl(section.backgroundImage)})` : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      {section.backgroundImage && (
        <div className="absolute inset-0 bg-black" style={{ opacity: section.overlayOpacity ?? 0.4 }} />
      )}
      <div className={`relative mx-auto ${section.variant === 'centered' ? 'text-center' : 'text-left'}`}>
        <h1 className="text-3xl md:text-5xl font-bold text-white mb-4">{section.title}</h1>
        {section.subtitle && <p className="text-lg md:text-xl text-white/80 mb-8">{section.subtitle}</p>}
        {section.ctaButton && (
          <a href={section.ctaButton.link || '#'} className="inline-block px-8 py-3 bg-white text-gray-900 font-semibold rounded-lg hover:bg-gray-100 transition">
            {section.ctaButton.text}
          </a>
        )}
      </div>
    </div>
  )
}

function ProductGridBlock({ section, ctx }: { section: any; ctx: any }) {
  const cols = section.columns || { mobile: 2, tablet: 3, desktop: 4 }
  // ปุ่มไหนเพิ่งกด "เพิ่มลงตะกร้า" สำเร็จ — ใช้สลับหน้าปุ่มเป็นติ๊กถูกชั่วครู่
  // (สินค้าที่มีตัวเลือกย่อยจะเด้ง popup ให้เลือกก่อน ยังไม่ถือว่าเพิ่มสำเร็จ)
  const [addedId, setAddedId] = useState<string | null>(null)
  useEffect(() => {
    if (!addedId) return
    const t = setTimeout(() => setAddedId(null), 1200)
    return () => clearTimeout(t)
  }, [addedId])
  // In editor, previewMode is passed so we use exact column count (Tailwind breakpoints
  // are based on viewport width, not container width, so responsive classes don't work inside the editor canvas)
  const gridClass = ctx.previewMode
    ? `grid grid-cols-${ctx.previewMode === 'mobile' ? cols.mobile : ctx.previewMode === 'tablet' ? cols.tablet : cols.desktop} gap-4`
    : `grid grid-cols-${cols.mobile} md:grid-cols-${cols.tablet} lg:grid-cols-${cols.desktop} gap-4`

  return (
    <div>
      {section.title && <h2 className="text-2xl font-bold text-center mb-2">{section.title}</h2>}
      {section.subtitle && <p className="text-gray-500 text-center mb-6">{section.subtitle}</p>}
      <div className={gridClass}>
        {(section.productIds || []).map((pid: string) => {
          const product = ctx.productMap.get(pid)
          if (!product) return null
          const cardRadius = section.cardRadius ?? 0
          const buttonRadius = section.buttonRadius ?? 0
          return (
            <div key={pid} className="bg-white border border-gray-200 overflow-hidden hover:shadow-md transition" style={{ borderRadius: cardRadius }}>
              {(() => {
                const compareAt = product.compareAtPrice ? Number(product.compareAtPrice) : 0
                const price = Number(product.price)
                const discountPct = compareAt > price && price > 0
                  ? Math.round(((compareAt - price) / compareAt) * 100)
                  : 0
                // เดิมรูปชนขอบการ์ดทั้ง 4 ด้าน — ตั้งระยะห่างได้ต่อ section
                const ip = section.imagePadding || {}
                const pad = {
                  paddingTop: ip.top ?? 0, paddingRight: ip.right ?? 0,
                  paddingBottom: ip.bottom ?? 0, paddingLeft: ip.left ?? 0,
                }
                const hasPad = (ip.top || ip.right || ip.bottom || ip.left) > 0
                const innerRadius = hasPad ? Math.max(0, cardRadius - Math.min(ip.top ?? 0, ip.left ?? 0)) : 0
                return (
                  <>
                    <div style={pad}>
                      <div
                        className="aspect-square relative bg-gray-100 overflow-hidden"
                        style={{ borderRadius: hasPad ? innerRadius : undefined }}
                      >
                        {product.image && (
                          <Image src={ctx.getImageUrl(product.image)} alt={product.name} fill className="object-cover" sizes="300px" />
                        )}
                        {discountPct > 0 && (
                          <div className="absolute top-0 left-0 bg-red-500 text-white text-[11px] font-bold px-2 py-1 rounded-br-lg">
                            -{discountPct}%
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="font-medium text-sm truncate">{product.name}</h3>
                      {section.showPrice && (
                        <div className="mt-1 flex items-baseline gap-1.5 flex-wrap">
                          <span className="text-sm font-bold" style={{ color: ctx.theme.primaryColor }}>{price.toLocaleString()}฿</span>
                          {discountPct > 0 && (
                            <span className="text-xs text-gray-400 line-through">{compareAt.toLocaleString()}฿</span>
                          )}
                        </div>
                      )}
                      {section.showStock && product.checkStock === true && product.stock > 0 && <p className="text-xs text-gray-400 mt-0.5">เหลือ {product.stock} ชิ้น</p>}
                      {section.showAddToCart && (() => {
                        const added = addedId === pid
                        const hasVariants = (product.variants?.length ?? 0) > 0
                        return (
                          <button
                            onClick={() => {
                              ctx.addToCart(product)
                              // ของที่มีตัวเลือกย่อยจะเปิด popup ให้เลือกก่อน — ยังไม่เข้าตะกร้า
                              // จริง เลยไม่ควรขึ้น "เพิ่มแล้ว"; buy-now ก็เด้งไป checkout อยู่แล้ว
                              if (!hasVariants && !ctx.singleItemMode) setAddedId(pid)
                            }}
                            className={`w-full mt-3 py-2.5 text-sm font-medium transition-all hover:shadow-md active:scale-[0.98] flex items-center justify-center gap-1.5 ${added ? 'animate-added-pop' : ''}`}
                            style={{
                              backgroundColor: added ? '#16a34a' : (ctx.orderButtonColor || ctx.theme.primaryColor),
                              color: 'white',
                              borderRadius: buttonRadius,
                            }}
                          >
                            {added ? (
                              <>
                                <svg className="w-4 h-4 animate-added-check" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                </svg>
                                เพิ่มแล้ว
                              </>
                            ) : (
                              <>
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
                                </svg>
                                {ctx.singleItemMode ? 'ซื้อสินค้า' : 'เพิ่มลงตะกร้า'}
                              </>
                            )}
                          </button>
                        )
                      })()}
                    </div>
                  </>
                )
              })()}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function TextBlock({ section }: { section: any }) {
  return (
    <div style={{ textAlign: section.alignment || 'left' }}>
      <div className="prose prose-gray max-w-none" dangerouslySetInnerHTML={{ __html: section.content || '' }} />
    </div>
  )
}

function ImageBlock({ section, getImageUrl, borderRadiusClass }: { section: any; getImageUrl: (url: string | null) => string; borderRadiusClass: string }) {
  const Wrapper = ({ children }: { children: React.ReactNode }) => {
    if (section.link) {
      return <a href={section.link} target={section.linkTarget || '_blank'} rel="noopener noreferrer" className="block">{children}</a>
    }
    return <>{children}</>
  }

  // Per-breakpoint max-width applied via a section-scoped style block.
  // Inline style can't carry media queries, so we emit a small <style>
  // matched by a unique id selector. SSR-safe (no client-side detection)
  // and tree-shakes to nothing when both values are the default 100%.
  const desktopWidth = section.desktopWidth || '100%'
  const mobileWidth = section.mobileWidth || '100%'
  const hasWidthOverride = desktopWidth !== '100%' || mobileWidth !== '100%'
  const widthStyleId = hasWidthOverride ? `img-w-${section.id}` : null
  const widthCss = widthStyleId
    ? `#${widthStyleId} > img,#${widthStyleId} > a > img,#${widthStyleId} > div > img{max-width:${mobileWidth};margin-left:auto;margin-right:auto;display:block}@media (min-width:768px){#${widthStyleId} > img,#${widthStyleId} > a > img,#${widthStyleId} > div > img{max-width:${desktopWidth}}}`
    : ''

  if (section.layout === 'full-width') {
    return (
      <>
        {widthStyleId && <style dangerouslySetInnerHTML={{ __html: widthCss }} />}
        <div id={widthStyleId ?? undefined}>
          <Wrapper>
            <div className="w-full"><Image src={getImageUrl(section.src)} alt={section.alt || ''} width={1200} height={600} className="w-full object-cover" /></div>
          </Wrapper>
        </div>
      </>
    )
  }

  if (section.layout === 'side-by-side' && section.images?.length > 0) {
    return (
      <div className={`grid grid-cols-2 gap-4`}>
        {section.images.map((img: any, i: number) => {
          const imgEl = <Image key={i} src={getImageUrl(img.src)} alt={img.alt || ''} width={600} height={400} className={`w-full object-cover ${borderRadiusClass}`} />
          return img.link ? <a key={i} href={img.link} target="_blank" rel="noopener noreferrer">{imgEl}</a> : imgEl
        })}
      </div>
    )
  }

  return (
    <>
      {widthStyleId && <style dangerouslySetInnerHTML={{ __html: widthCss }} />}
      <div id={widthStyleId ?? undefined}>
        <Wrapper>
          <div>
            <Image src={getImageUrl(section.src)} alt={section.alt || ''} width={800} height={400} className={`w-full object-cover ${borderRadiusClass}`} />
          </div>
        </Wrapper>
      </div>
    </>
  )
}

// Responsive card grid. Cols per breakpoint come from
// section.columns.{mobile,tablet,desktop}; we render the values
// into Tailwind classes via the small COL_CLASS map so the JIT
// compiler can pick them up at build time (dynamic class names
// would be tree-shaken away). Each cell stays a card with
// image-on-top / text-below; cards without images collapse the
// image slot so a text-only grid still looks tidy.
const GRID_COL_CLASS = {
  mobile: { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3' },
  tablet: { 1: 'md:grid-cols-1', 2: 'md:grid-cols-2', 3: 'md:grid-cols-3', 4: 'md:grid-cols-4' },
  desktop: { 1: 'lg:grid-cols-1', 2: 'lg:grid-cols-2', 3: 'lg:grid-cols-3', 4: 'lg:grid-cols-4', 5: 'lg:grid-cols-5', 6: 'lg:grid-cols-6' },
} as const

const GRID_GAP_CLASS: Record<string, string> = {
  sm: 'gap-2',
  md: 'gap-4',
  lg: 'gap-8',
}

function GridBlock({ section, theme, getImageUrl }: { section: any; theme: PageTheme; getImageUrl: (url: string | null) => string }) {
  const cols = section.columns || { mobile: 1, tablet: 2, desktop: 3 }
  const items: Array<{ image?: string; title?: string; description?: string; buttonText?: string; buttonLink?: string }> = section.items || []
  const radius = typeof section.cardRadius === 'number' ? section.cardRadius : 12
  const cardBg = section.cardBackground || '#ffffff'

  const mobileClass = GRID_COL_CLASS.mobile[(cols.mobile ?? 1) as 1 | 2 | 3] || 'grid-cols-1'
  const tabletClass = GRID_COL_CLASS.tablet[(cols.tablet ?? 2) as 1 | 2 | 3 | 4] || 'md:grid-cols-2'
  const desktopClass = GRID_COL_CLASS.desktop[(cols.desktop ?? 3) as 1 | 2 | 3 | 4 | 5 | 6] || 'lg:grid-cols-3'
  const gapClass = GRID_GAP_CLASS[section.gap || 'md']

  return (
    <div>
      {(section.title || section.subtitle) && (
        <div className="text-center mb-6">
          {section.title && <h2 className="text-2xl md:text-3xl font-bold mb-2">{section.title}</h2>}
          {section.subtitle && <p className="text-gray-600">{section.subtitle}</p>}
        </div>
      )}
      <div className={`grid ${mobileClass} ${tabletClass} ${desktopClass} ${gapClass}`}>
        {items.map((it, i) => {
          // Border off by default — only draw it when admin picks a
          // card background that's lighter than the page or sets a
          // non-zero radius. Keeps the "empty grid of 3 placeholders"
          // looking clean instead of boxed-in.
          const hasFrame = radius > 0 || (cardBg && cardBg.toLowerCase() !== '#ffffff' && cardBg.toLowerCase() !== 'transparent')
          const inner = (
            <div
              className={`h-full flex flex-col overflow-hidden ${hasFrame ? 'border border-gray-100' : ''}`}
              style={{ borderRadius: radius, background: hasFrame ? cardBg : 'transparent' }}
            >
              {it.image && (
                <div className="w-full aspect-video bg-gray-50">
                  <Image
                    src={getImageUrl(it.image)}
                    alt={it.title || ''}
                    width={480}
                    height={270}
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              {(it.title || it.description || it.buttonText) && (
                <div className="p-4 flex-1 flex flex-col">
                  {it.title && <h3 className="font-semibold text-base mb-1">{it.title}</h3>}
                  {it.description && <p className="text-sm text-gray-600 flex-1">{it.description}</p>}
                  {it.buttonText && (
                    <a
                      href={it.buttonLink || '#'}
                      className="mt-3 inline-flex items-center justify-center px-3 py-1.5 text-sm font-semibold rounded-md text-white"
                      style={{ backgroundColor: theme.primaryColor }}
                    >
                      {it.buttonText}
                    </a>
                  )}
                </div>
              )}
            </div>
          )
          // When buttonLink is set + no button text, make the whole card
          // clickable. Otherwise the button is the click target.
          return it.buttonLink && !it.buttonText ? (
            <a key={i} href={it.buttonLink} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition">{inner}</a>
          ) : (
            <div key={i}>{inner}</div>
          )
        })}
      </div>
    </div>
  )
}

function CTABlock({ section, theme }: { section: any; theme: PageTheme }) {
  return (
    <div className="text-center" style={{ backgroundColor: section.backgroundColor || theme.primaryColor + '10' }}>
      <h2 className="text-2xl md:text-3xl font-bold mb-3">{section.title}</h2>
      {section.subtitle && <p className="text-gray-600 mb-6">{section.subtitle}</p>}
      <a
        href={section.buttonLink || '#'}
        className="inline-block px-8 py-3 text-white font-semibold rounded-lg hover:opacity-90 transition"
        style={{ backgroundColor: section.buttonColor || theme.primaryColor }}
      >
        {section.buttonText}
      </a>
    </div>
  )
}

function CountdownBlock({ section, theme }: { section: any; theme: PageTheme }) {
  const [timeLeft, setTimeLeft] = useState(() => {
    const diff = Math.max(0, new Date(section.endDate).getTime() - Date.now())
    return diff
  })

  useEffect(() => {
    if (timeLeft <= 0) return
    const timer = setInterval(() => {
      const diff = Math.max(0, new Date(section.endDate).getTime() - Date.now())
      setTimeLeft(diff)
      if (diff <= 0) clearInterval(timer)
    }, 1000)
    return () => clearInterval(timer)
  }, [section.endDate, timeLeft])

  const days = Math.floor(timeLeft / (1000 * 60 * 60 * 24))
  const hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const mins = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
  const secs = Math.floor((timeLeft % (1000 * 60)) / 1000)

  if (timeLeft <= 0) {
    return <div className="text-center text-gray-500">{section.expiredText || 'โปรโมชั่นสิ้นสุดแล้ว'}</div>
  }

  return (
    <div className="text-center" style={{ backgroundColor: section.backgroundColor || theme.primaryColor }}>
      <p className="text-white font-medium mb-4">{section.title}</p>
      <div className="flex justify-center gap-3">
        {[{ value: days, label: 'วัน' }, { value: hours, label: 'ชั่วโมง' }, { value: mins, label: 'นาที' }, { value: secs, label: 'วินาที' }].map((t, i) => (
          <div key={i} className="bg-white/20 rounded-lg px-3 py-2 min-w-[60px]">
            <div className="text-2xl font-bold text-white tabular-nums">{String(t.value).padStart(2, '0')}</div>
            <div className="text-[10px] text-white/80">{t.label}</div>
          </div>
        ))}
      </div>
    </div>
  )
}

function TestimonialBlock({ section, borderRadiusClass }: { section: any; borderRadiusClass: string }) {
  return (
    <div>
      {section.title && <h2 className="text-2xl font-bold text-center mb-6">{section.title}</h2>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {(section.items || []).map((item: any, i: number) => (
          <div key={i} className={`p-4 bg-gray-50 ${borderRadiusClass}`}>
            <div className="flex items-center gap-3 mb-3">
              {item.avatar && <Image src={item.avatar} alt={item.name} width={40} height={40} className="rounded-full" />}
              <div>
                <p className="font-medium text-sm">{item.name}</p>
                {item.rating && <div className="text-yellow-400 text-sm">{'★'.repeat(item.rating)}{'☆'.repeat(5 - item.rating)}</div>}
              </div>
            </div>
            <p className="text-sm text-gray-600">{item.text}</p>
          </div>
        ))}
      </div>
    </div>
  )
}

function FAQBlock({ section }: { section: any }) {
  return (
    <div>
      {section.title && <h2 className="text-2xl font-bold text-center mb-6">{section.title}</h2>}
      <div className="space-y-3">
        {(section.items || []).map((item: any, i: number) => (
          <details key={i} className="group bg-gray-50 rounded-lg">
            <summary className="py-3 cursor-pointer font-medium text-sm flex justify-between items-center px-1">
              {item.question}
              <svg className="w-4 h-4 group-open:rotate-180 transition" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
              </svg>
            </summary>
            <div className="pb-3 text-sm text-gray-600 px-1">{item.answer}</div>
          </details>
        ))}
      </div>
    </div>
  )
}

function VideoBlock({ section }: { section: any }) {
  const url = section.url || ''

  // Extract YouTube/Vimeo embed URL
  let embedUrl = ''
  const ytMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]+)/)
  const vimeoMatch = url.match(/vimeo\.com\/(\d+)/)

  if (ytMatch) embedUrl = `https://www.youtube.com/embed/${ytMatch[1]}?rel=0`
  else if (vimeoMatch) embedUrl = `https://player.vimeo.com/video/${vimeoMatch[1]}`
  else if (url.includes('embed')) embedUrl = url

  if (!embedUrl) {
    return <div className="text-center text-gray-400 text-sm">กรุณาใส่ลิงก์ YouTube หรือ Vimeo</div>
  }

  return (
    <div>
      <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
        <iframe
          src={embedUrl}
          className="absolute inset-0 w-full h-full rounded-xl"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          loading="lazy"
        />
      </div>
    </div>
  )
}

function DividerBlock({ section }: { section: any }) {
  if (section.style === 'space') return <div className="py-8" />
  if (section.style === 'dots') return <div className="py-4 text-center text-gray-300 tracking-widest">...</div>
  return <hr className="border-gray-200 my-4" />
}

// --- Image Slider ---

function ImageSliderBlock({ section, getImageUrl }: { section: any; getImageUrl: (url: string | null) => string }) {
  const slides = section.slides || []
  const [current, setCurrent] = useState(0)

  const next = useCallback(() => setCurrent(i => (i + 1) % slides.length), [slides.length])
  const prev = () => setCurrent(i => (i - 1 + slides.length) % slides.length)

  // Autoplay
  useEffect(() => {
    if (!section.autoplay || slides.length <= 1) return
    const interval = setInterval(next, section.autoplayInterval || 4000)
    return () => clearInterval(interval)
  }, [section.autoplay, section.autoplayInterval, slides.length, next])

  if (slides.length === 0) {
    return <div className="w-full h-48 bg-slate-100 flex items-center justify-center text-slate-400">ยังไม่มีรูปในสไลด์</div>
  }

  const slide = slides[current]

  const SlideContent = (
    <div className="relative w-full overflow-hidden" style={{ height: section.height || '400px' }}>
      <Image
        src={getImageUrl(slide.src)}
        alt={slide.alt || ''}
        fill
        className="object-cover transition-opacity duration-500"
        sizes="100vw"
      />

      {/* Caption */}
      {slide.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-6">
          <p className="text-white text-lg font-medium">{slide.caption}</p>
        </div>
      )}

      {/* Arrows */}
      {section.showArrows !== false && slides.length > 1 && (
        <>
          <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" /></svg>
          </button>
          <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-white/80 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
          </button>
        </>
      )}

      {/* Dots */}
      {section.showDots !== false && slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_: any, i: number) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`w-2.5 h-2.5 rounded-full transition ${i === current ? 'bg-white scale-110' : 'bg-white/50 hover:bg-white/80'}`}
            />
          ))}
        </div>
      )}
    </div>
  )

  // Wrap with link if slide has a link
  if (slide.link) {
    return <a href={slide.link} target="_blank" rel="noopener noreferrer">{SlideContent}</a>
  }

  return SlideContent
}

// --- Popup ---

function PopupBlock({ section, theme, getImageUrl }: { section: any; theme: PageTheme; getImageUrl: (url: string | null) => string }) {
  const [show, setShow] = useState(false)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    // Check if already shown in this session
    if (section.showOnce) {
      const key = `popup-shown-${section.id}`
      if (sessionStorage.getItem(key)) return
    }

    const trigger = section.trigger || 'delay'

    if (trigger === 'on_load') {
      setShow(true)
      markShown()
    } else if (trigger === 'delay') {
      const timer = setTimeout(() => { setShow(true); markShown() }, section.delayMs || 3000)
      return () => clearTimeout(timer)
    } else if (trigger === 'exit_intent') {
      const handler = (e: MouseEvent) => {
        if (e.clientY <= 0 && !dismissed) {
          setShow(true)
          markShown()
          document.removeEventListener('mouseout', handler)
        }
      }
      document.addEventListener('mouseout', handler)
      return () => document.removeEventListener('mouseout', handler)
    } else if (trigger === 'scroll') {
      const handler = () => {
        const scrollPercent = (window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100
        if (scrollPercent >= (section.scrollPercent || 50) && !dismissed) {
          setShow(true)
          markShown()
          window.removeEventListener('scroll', handler)
        }
      }
      window.addEventListener('scroll', handler)
      return () => window.removeEventListener('scroll', handler)
    }

    function markShown() {
      if (section.showOnce) {
        sessionStorage.setItem(`popup-shown-${section.id}`, '1')
      }
    }
  }, [section.id, section.trigger, section.delayMs, section.scrollPercent, section.showOnce, dismissed])

  const handleClose = () => {
    setShow(false)
    setDismissed(true)
  }

  const handleCTA = () => {
    const cta = section.ctaButton
    if (!cta) return handleClose()

    switch (cta.action) {
      case 'close': handleClose(); break
      case 'link': if (cta.link) window.open(cta.link, '_blank'); handleClose(); break
      case 'chat_facebook': window.open('https://m.me/', '_blank'); handleClose(); break
      case 'chat_line': window.open('https://line.me/', '_blank'); handleClose(); break
      default: handleClose()
    }
  }

  if (!show) return null

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={section.overlayClose !== false ? handleClose : undefined}
      />

      {/* Modal */}
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-popup-in"
        style={{ backgroundColor: section.backgroundColor || '#FFFFFF' }}
      >
        {/* Close button */}
        <button onClick={handleClose} className="absolute top-3 right-3 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20 text-gray-600 z-10">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        {/* Image */}
        {section.image && (
          <div className="relative w-full h-48">
            <Image src={getImageUrl(section.image)} alt="" fill className="object-cover" sizes="400px" />
          </div>
        )}

        {/* Content */}
        <div className="p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-2">{section.title}</h3>
          {section.content && (
            <div className="prose prose-sm prose-gray max-w-none mb-4" dangerouslySetInnerHTML={{ __html: section.content }} />
          )}

          {/* CTA Button */}
          {section.ctaButton && (
            <button
              onClick={handleCTA}
              className="w-full py-3 text-white font-medium rounded-xl transition hover:opacity-90"
              style={{ backgroundColor: theme.primaryColor }}
            >
              {section.ctaButton.text}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}

// --- Variant Picker Popup ---

function VariantPickerPopup({
  product,
  theme,
  getImageUrl,
  singleItemMode,
  onSelect,
  onClose,
}: {
  product: Product
  theme: PageTheme
  getImageUrl: (url: string | null | undefined) => string
  singleItemMode?: boolean
  onSelect: (variant: ProductVariant) => void
  onClose: () => void
}) {
  const options = product.options || []
  const variants = product.variants || []

  // Two-level model: the FIRST declared option is the global "main" axis;
  // everything else is a PER-MAIN-VALUE sub-option derived from the variants
  // of the chosen group. This handles both the new per-variant products
  // (heterogeneous subs per main value) and legacy global-axis products
  // (same sub axis across every main value).
  const mainName = options[0]?.name ?? Object.keys(variants[0]?.options || {})[0] ?? ''
  const mainValues = options[0]?.values ?? Array.from(new Set(variants.map(v => v.options[mainName]).filter(Boolean)))

  // Track selected option values
  const [selected, setSelected] = useState<Record<string, string>>({})
  const [currentImage, setCurrentImage] = useState(product.image)

  const selectedMain = mainName ? selected[mainName] : undefined
  const group = selectedMain != null ? variants.filter(v => v.options[mainName] === selectedMain) : []

  // Sub-option keys present in the selected group (ordered, excluding main).
  const subKeys: string[] = []
  for (const v of group) {
    for (const k of Object.keys(v.options)) {
      if (k !== mainName && v.options[k] && !subKeys.includes(k)) subKeys.push(k)
    }
  }
  const subValuesOf = (k: string) => Array.from(new Set(group.map(v => v.options[k]).filter(Boolean)))

  // Matched variant: every sub key satisfied — or, when the group has no
  // sub-option, the single bare variant of that main value.
  const matchedVariant = selectedMain != null
    ? group.find(v => subKeys.every(k => v.options[k] === selected[k]))
    : undefined

  // Picking the main value resets sub selections (subs differ per main value).
  const pickMain = (value: string) => {
    setSelected({ [mainName]: value })
    const g = variants.filter(v => v.options[mainName] === value)
    setCurrentImage(g.find(v => v.image)?.image || product.image)
  }
  const pickSub = (key: string, value: string) => {
    const newSelected = { ...selected, [key]: value }
    setSelected(newSelected)
    const partial = variants.find(v => Object.entries(newSelected).every(([k, val]) => v.options[k] === val))
    setCurrentImage(partial?.image || currentImage || product.image)
  }

  const allOptionsSelected = !!selectedMain && subKeys.every(k => selected[k])
  const displayPrice = matchedVariant ? matchedVariant.price : product.price
  const displayStock = stockOf(product, matchedVariant)
  const isOutOfStock = !!matchedVariant && !variantInStock(product, matchedVariant)

  return (
    <div className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      <div className="relative bg-white w-full max-w-lg sm:rounded-2xl rounded-t-2xl shadow-2xl overflow-hidden animate-popup-in max-h-[90vh] flex flex-col">
        {/* Close */}
        <button onClick={onClose} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-black/10 hover:bg-black/20">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>

        {/* Product Image */}
        <div className="relative w-full aspect-square max-h-[300px] bg-gray-100 flex-shrink-0">
          {currentImage ? (
            <Image
              src={getImageUrl(currentImage)}
              alt={product.name}
              fill
              className="object-contain transition-all duration-300"
              sizes="500px"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-gray-300">
              <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5 overflow-y-auto flex-1">
          <h3 className="text-lg font-bold text-gray-900 mb-1">{product.name}</h3>
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl font-bold" style={{ color: theme.primaryColor }}>{displayPrice.toLocaleString()}฿</span>
            {/* ร้านที่ไม่นับสต๊อกได้ Infinity — ไม่มีอะไรให้บอก ซ่อนไปเลย
                ไม่งั้นขึ้น "เหลือ Infinity ชิ้น" */}
            {matchedVariant && Number.isFinite(displayStock) && displayStock > 0 && (
              <span className="text-xs text-gray-400">เหลือ {displayStock} ชิ้น</span>
            )}
            {isOutOfStock && (
              <span className="text-xs text-red-500 font-medium">สินค้าหมด</span>
            )}
          </div>

          {/* Main option selector */}
          {mainName && (
            <div className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {mainName}
                {selectedMain && <span className="text-gray-400 font-normal ml-1">: {selectedMain}</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {mainValues.map(value => {
                  const isSelected = selectedMain === value
                  const hasStock = variants.some(v => v.options[mainName] === value && variantInStock(product, v))
                  const variantWithImage = variants.find(v => v.options[mainName] === value && v.image)
                  return (
                    <button
                      key={value}
                      onClick={() => pickMain(value)}
                      disabled={!hasStock}
                      className={`relative px-4 py-2 text-sm rounded-lg border-2 transition-all ${isSelected ? 'border-current font-medium shadow-sm' : 'border-gray-200 hover:border-gray-300'} ${!hasStock ? 'opacity-30 cursor-not-allowed line-through' : 'cursor-pointer'}`}
                      style={isSelected ? { borderColor: theme.primaryColor, color: theme.primaryColor } : {}}
                    >
                      {variantWithImage && (
                        <Image src={getImageUrl(variantWithImage.image)} alt={value} width={24} height={24} className="inline-block rounded-sm mr-1.5 -ml-1 object-cover" />
                      )}
                      {value}
                    </button>
                  )
                })}
              </div>
            </div>
          )}

          {/* Sub-option selectors — only after a main value is chosen, derived
              from that group's variants (so each main value can have its own). */}
          {selectedMain && subKeys.map(key => (
            <div key={key} className="mb-4">
              <p className="text-sm font-medium text-gray-700 mb-2">
                {key}
                {selected[key] && <span className="text-gray-400 font-normal ml-1">: {selected[key]}</span>}
              </p>
              <div className="flex flex-wrap gap-2">
                {subValuesOf(key).map(value => {
                  const isSelected = selected[key] === value
                  const hasStock = group.some(v =>
                    v.options[key] === value &&
                    variantInStock(product, v) &&
                    subKeys.every(k => k === key || !selected[k] || v.options[k] === selected[k])
                  )
                  const variantWithImage = group.find(v => v.options[key] === value && v.image)
                  return (
                    <button
                      key={value}
                      onClick={() => pickSub(key, value)}
                      disabled={!hasStock}
                      className={`relative px-4 py-2 text-sm rounded-lg border-2 transition-all ${isSelected ? 'border-current font-medium shadow-sm' : 'border-gray-200 hover:border-gray-300'} ${!hasStock ? 'opacity-30 cursor-not-allowed line-through' : 'cursor-pointer'}`}
                      style={isSelected ? { borderColor: theme.primaryColor, color: theme.primaryColor } : {}}
                    >
                      {variantWithImage && (
                        <Image src={getImageUrl(variantWithImage.image)} alt={value} width={24} height={24} className="inline-block rounded-sm mr-1.5 -ml-1 object-cover" />
                      )}
                      {value}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}

          {/* Variant Images Preview (thumbnails of available variant images) */}
          {variants.filter(v => v.image).length > 1 && (
            <div className="mt-2 mb-4">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {/* Product default image */}
                <button
                  onClick={() => setCurrentImage(product.image)}
                  className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition ${
                    currentImage === product.image ? 'border-current' : 'border-gray-200'
                  }`}
                  style={currentImage === product.image ? { borderColor: theme.primaryColor } : {}}
                >
                  {product.image && <Image src={getImageUrl(product.image)} alt="" width={56} height={56} className="object-cover w-full h-full" />}
                </button>
                {/* Variant images */}
                {variants.filter(v => v.image && v.image !== product.image).map(v => (
                  <button
                    key={v.id}
                    onClick={() => setCurrentImage(v.image)}
                    className={`flex-shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 transition ${
                      currentImage === v.image ? 'border-current' : 'border-gray-200'
                    }`}
                    style={currentImage === v.image ? { borderColor: theme.primaryColor } : {}}
                  >
                    <Image src={getImageUrl(v.image)} alt={v.name} width={56} height={56} className="object-cover w-full h-full" />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Add to Cart Button */}
        <div className="p-4 border-t border-gray-100 flex-shrink-0">
          <button
            onClick={() => matchedVariant && onSelect(matchedVariant)}
            disabled={!allOptionsSelected || !matchedVariant || isOutOfStock}
            className="w-full py-3 text-white font-medium rounded-xl transition hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ backgroundColor: theme.primaryColor }}
          >
            {!allOptionsSelected
              ? 'กรุณาเลือกตัวเลือกให้ครบ'
              : isOutOfStock
                ? 'สินค้าหมด'
                : `${singleItemMode ? 'ซื้อสินค้า' : 'เพิ่มลงตะกร้า'} — ${displayPrice.toLocaleString()}฿`
            }
          </button>
        </div>
      </div>
    </div>
  )
}

// --- Cart Dropdown Content ---

function CartDropdownContent({ cart, cartCount, cartTotal, theme, shopId, salePageId, getImageUrl, updateQuantity, onCheckout, onClose }: {
  cart: CartItem[]
  cartCount: number
  cartTotal: number
  theme: PageTheme
  shopId: string
  salePageId?: string
  getImageUrl: (url: string | null | undefined) => string
  updateQuantity: (id: string, delta: number) => void
  onCheckout: () => void
  onClose: () => void
}) {
  return (
    <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 flex flex-col max-h-[70vh]">
      <div className="px-4 py-3 border-b border-gray-100 flex justify-between items-center flex-shrink-0">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
          </svg>
          <span className="font-semibold text-gray-800">ตะกร้า</span>
          {cartCount > 0 && (
            <span className="text-xs font-medium px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: theme.primaryColor }}>{cartCount}</span>
          )}
        </div>
        <div className="flex items-center gap-1">
          {cart.length > 0 && (
            <CopyCartButton
              shopId={shopId}
              salePageId={salePageId}
              items={cart}
              label="คัดลอก"
              className="inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 transition disabled:opacity-50"
            />
          )}
          <button onClick={onClose} className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors">
            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto">
        {cart.length === 0 ? (
          <div className="py-12 text-center">
            <svg className="w-12 h-12 text-gray-200 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 100 4 2 2 0 000-4z" />
            </svg>
            <p className="text-sm text-gray-400">ยังไม่มีสินค้าในตะกร้า</p>
          </div>
        ) : (
          <div className="p-3 space-y-2">
            {cart.map(item => (
              <div key={item.productId} className="flex gap-3 p-2 rounded-xl hover:bg-gray-50 transition-colors">
                {item.image && (
                  <Image src={getImageUrl(item.image)} alt={item.name} width={56} height={56} className="rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800 truncate">{item.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{item.price.toLocaleString()}฿ / ชิ้น</p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <button onClick={() => updateQuantity(item.productId, -1)} className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-md text-xs transition-colors">−</button>
                    <span className="text-sm font-medium w-6 text-center tabular-nums">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.productId, 1)} className="w-6 h-6 flex items-center justify-center bg-gray-100 hover:bg-gray-200 rounded-md text-xs transition-colors">+</button>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 flex flex-col items-end gap-1">
                  <button onClick={() => updateQuantity(item.productId, -item.quantity)} className="w-6 h-6 flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-500 rounded-full transition-colors" title="ลบ">
                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                  <p className="text-sm font-semibold text-gray-800">{(item.price * item.quantity).toLocaleString()}฿</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {cart.length > 0 && (
        <div className="p-4 border-t border-gray-100 flex-shrink-0 space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-500">รวม ({cartCount} ชิ้น)</span>
            <span className="text-xl font-bold" style={{ color: theme.primaryColor }}>{cartTotal.toLocaleString()}฿</span>
          </div>
          <button onClick={onCheckout} className="w-full py-3 text-white font-medium rounded-xl text-center transition-all hover:opacity-90 active:scale-[0.98]" style={{ backgroundColor: theme.primaryColor }}>
            สั่งซื้อเลย
          </button>
        </div>
      )}
    </div>
  )
}
